import { useContext, useEffect, useState } from 'react';
import { UseContactsCompareForDiffContactFragment } from '#types';
import { Differences } from '../lib/helpers/deepCompareAndDiff';
import { gql } from '@apollo/client';
import {
	contactDiffers,
	ContactsContext,
} from '../context/ContactsContextProvider/ContactsContextProvider';

export const useContactsCompareForDiffContactFragment = gql`
	fragment useContactsCompareForDiffContact on Contact {
		id
		firstName
		lastName
		companyName
		customer {
			id
			email
		}
	}
`;

export const useContactsCompareForDiff = (
	contact?: UseContactsCompareForDiffContactFragment | null,
	differencesProp?: Differences,
	/* only compare contacts if we are in a tabbed view, where contacts context is a thing,
	otherwise we don't need contractEdits calculations because nothing can be changed in the
	"edit" tab */
	tabbedView = true
) => {
	const [ differences, setContractEdits ] = useState<Differences | undefined>(
		undefined
	);

	const contactsContext = useContext( ContactsContext );
	const contextContacts = contactsContext?.contacts;
	const firstContextContact = contextContacts?.[ 0 ];
	useEffect( () => {
		// only compare contacts if we are in a tabbed view, where contacts context is a thing
		if ( tabbedView ) {
			// If contextContacts is undefined, the context hasn't been initialized yet
			// (the proposal's contact hasn't been set into context). Don't flag a diff
			// until the context has a value — only then can a real change be detected.
			if ( contextContacts === undefined ) {
				setContractEdits( differencesProp ?? undefined );
				return;
			}
			const contactsMatch = !contactDiffers(
				contact,
				firstContextContact,
				false
			);
			const contactExistsAndDoesNotMatch =
				contact && contactDiffers( contact, firstContextContact, false );
			setContractEdits( ( prevState ) => {
				if ( !differencesProp && contactsMatch ) {
					return prevState ? undefined : prevState;
				} else {
					return {
						...differencesProp,
						...( contactExistsAndDoesNotMatch
							? {
								contact: {
									oldValue: contact,
									newValue: firstContextContact,
								},
							  }
							: {} ),
					};
				}
			} );
		}
	}, [
		contact,
		contextContacts,
		differencesProp,
		firstContextContact,
		tabbedView,
	] );

	return { differences };
};
